import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Package, ChefHat, Bike, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STEPS = [
  { icon: CheckCircle2, label: "Order confirmed", done: true },
  { icon: ChefHat, label: "Chef is cooking", done: true },
  { icon: Package, label: "Packed & ready", done: true },
  { icon: Bike, label: "Out for delivery", done: false, active: true },
];

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [tracked, setTracked] = useState(null);

  const track = (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setTracked(orderId.trim().toUpperCase());
  };

  return (
    <div data-testid="track-order-page" className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Track order</p>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-neutral-900">Where's my food?</h1>
        <p className="mt-4 text-neutral-600">Enter your order number to see live status.</p>
      </motion.div>

      <form onSubmit={track} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="track-form">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <Input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="e.g. JL-1042" data-testid="track-input" className="h-12 pl-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
        </div>
        <Button type="submit" data-testid="track-submit" className="h-12 px-8 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">Track</Button>
      </form>

      {tracked && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-12 border border-neutral-200 bg-white p-8" data-testid="track-result">
          <div className="flex items-center justify-between border-b border-neutral-100 pb-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-400">Order</p>
              <p className="font-display text-2xl font-bold text-neutral-900">{tracked}</p>
            </div>
            <span className="bg-[#f5a623]/15 text-[#b8791a] text-sm px-3 py-1">Estimated 15 min</span>
          </div>
          <div className="space-y-6">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-4" data-testid={`track-step-${i}`}>
                <span className={`w-11 h-11 flex items-center justify-center ${s.done ? "bg-[#d00000] text-white" : s.active ? "bg-[#d00000]/10 text-[#d00000] border border-[#d00000]" : "bg-neutral-100 text-neutral-400"}`}>
                  <s.icon className="w-5 h-5" />
                </span>
                <div className="flex-1">
                  <p className={`font-medium ${s.done || s.active ? "text-neutral-900" : "text-neutral-400"}`}>{s.label}</p>
                  {s.active && <p className="text-sm text-[#d00000]">In progress…</p>}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
